import React, { useEffect, useState } from "react";
import { Send, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, StatusPill } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { AIOutputDisclaimer } from "@/components/Disclaimer";
import AIFeedback from "@/components/AIFeedback";
import Icd10Autocomplete from "@/components/Icd10Autocomplete";
import ShowReasoning from "@/components/ShowReasoning";
import { exportToPDF } from "@/lib/pdf";

export default function Referrals() {
  const [refs, setRefs] = useState([]);
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({ patient_id: "", to_specialty: "", to_provider_name: "", diagnosis: "", urgency: "routine", notes: "" });
  const [packet, setPacket] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals").then((r)=>setRefs(r.data));
  useEffect(() => {
    load(); api.get("/patients").then((r)=>{ setPatients(r.data); setForm(f=>({...f, patient_id: r.data[0]?.id||""})); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/referrals", form);
    toast.success("Referral sent");
    setForm({ ...form, to_specialty: "", to_provider_name: "", diagnosis: "", notes: "" });
    load();
  };

  const packetGen = async (id) => {
    setBusy(true);
    try {
      const data = await aiJob(`/referrals/${id}/generate-packet`, {});
      setPacket({ id, text: data.final, score: data.score, reasoning: data.reasoning, verification_footer: data.verification_footer });
      toast.success("Referral packet drafted");
    } catch { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/referrals/${id}`, { status });
    load();
  };

  const columns = [
    { key: "patient_id", label: "Patient", render: (r)=>patients.find(p=>p.id===r.patient_id)?.name || "—" },
    { key: "to_specialty", label: "Specialist" },
    { key: "diagnosis", label: "Diagnosis" },
    { key: "sent_date", label: "Sent", render: (r)=>new Date(r.sent_date).toLocaleDateString() },
    { key: "status", label: "Status", render: (r)=><StatusPill status={r.status} /> },
    { key: "actions", label: "Actions", sortable: false, render: (r) => (
      <div className="flex gap-1.5">
        <button onClick={()=>packetGen(r.id)} className="text-xs px-2 py-1 rounded border border-brand hover:bg-brand-bg" data-testid={`packet-${r.id}`}>Packet</button>
        <select value={r.status} onChange={(e)=>setStatus(r.id, e.target.value)} className="text-xs border border-brand rounded">
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="no_response">No Response</option>
        </select>
      </div>
    )},
  ];

  return (
    <>
      <PageHeader eyebrow="Module 5" title="Specialist Referral Hub"
        subtitle="Create referrals, auto-build referral packets via topology agent, track status end-to-end." testid="referrals-page" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="New Referral" testid="referral-form">
          <form onSubmit={submit} className="space-y-2">
            <select value={form.patient_id} onChange={(e)=>setForm({...form, patient_id:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="ref-patient">
              {patients.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input placeholder="Specialty (e.g. Cardiology)" value={form.to_specialty} onChange={(e)=>setForm({...form, to_specialty:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" data-testid="ref-specialty" />
            <input placeholder="Specialist name (optional)" value={form.to_provider_name} onChange={(e)=>setForm({...form, to_provider_name:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <Icd10Autocomplete
              value={form.diagnosis}
              onChange={(v)=>setForm({...form, diagnosis: v})}
              placeholder="Diagnosis — ICD-10 code or keyword"
              testid="ref-diagnosis-icd"
              single
            />
            <input placeholder="Diagnosis (free text fallback)" value={form.diagnosis} onChange={(e)=>setForm({...form, diagnosis:e.target.value})} required
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm mt-2" data-testid="ref-diagnosis" />
            <select value={form.urgency} onChange={(e)=>setForm({...form, urgency:e.target.value})}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm">
              <option value="routine">Routine</option><option value="urgent">Urgent</option><option value="stat">STAT</option>
            </select>
            <textarea placeholder="Notes…" value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} rows={3}
              className="w-full px-3 py-2 bg-white border border-brand rounded-md text-sm" />
            <button type="submit" className="w-full bg-brand-primary text-white py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center justify-center gap-2" data-testid="ref-submit">
              <Send className="w-4 h-4" /> Send Referral
            </button>
          </form>
        </SectionCard>

        <div className="lg:col-span-2">
          <SectionCard title="Referral Packet" subtitle={packet ? `Score ${packet.score}/100` : "Use 'Packet' on any row."}
            actions={packet && <button onClick={()=>exportToPDF("Referral Packet", packet.text)} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</button>}>
            {busy && <div className="flex items-center gap-2 text-sm text-brand-muted"><Loader2 className="w-4 h-4 animate-spin" /> Drafting packet…</div>}
            {!busy && packet && (
              <>
                <AIOutputDisclaimer />
                <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text max-h-96 overflow-y-auto" data-testid="packet-text">{packet.text}</pre>
                {packet.verification_footer && (
                  <div className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="packet-verification-footer">{packet.verification_footer}</div>
                )}
                <ShowReasoning reasoning={packet.reasoning} score={packet.score} testid="packet-reasoning" />
                <AIFeedback module="referrals" outputId={packet.id} outputType="referral_packet" testid="packet-feedback" />
              </>
            )}
            {!busy && !packet && <div className="text-sm text-brand-muted">No packet drafted yet.</div>}
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Referral Tracking" testid="referrals-table-section">
        <DataTable data={refs} columns={columns} searchKeys={["to_specialty","diagnosis","status"]} testid="referrals-table" />
      </SectionCard>
    </>
  );
}
